'use strict';

namespace Core.Hotels {

    angular.module('core')
        .service('hotelFromSession', hotelFromSession);

    type FieldType = Core.FieldType;
    type IHttpService = Core.IHttpService;
    type FieldGroupService = Core.FieldGroupService;
    type ILoggingService = Core.ILoggingService;

    export interface HotelFromSessionService {
        getHotel(searchId: string, estabId: number, tripId: string): ng.IPromise<HotelWithRooms>;
        getRoomTypes(estabId: number, searchId: string, boardTypeFilter: BoardType[]);
    }

//this is an internal representation of the hotel withrooms that is returned by the api
//to the extent that we care about the types. Mostly this is required to gloss over the
//flattening of the rooms structure
    interface ApiHotelWithRooms {
        RequestedRooms: RequestedRoom[];
    }

    interface RequestedRoom {
        BoardTypeMinPrices: BoardTypeMinPriceFull[];
        Availability: ApiRoomAvailability[];
    }
    /*
     This exists to retrieve a single hotel's availability results from the cache.
     It is used by the map directive for showing information windows and is also used
     by the hotel page when in search mode. Calls are cached for the duration of a user's
     session
     */
    function hotelFromSession($filter,
                              $location: ng.ILocationService,
                              $http: IHttpService,
                              $cacheFactory: ng.ICacheFactoryService,
                              $q: ng.IQService,
                              imageService,
                              $injector,
                              $timeout: ng.ITimeoutService,
                              tripService: TripService,
                              fieldGroupService: FieldGroupService,
                              loggingService: ILoggingService,
                              occupancyService: Core.OccupancyService) {
        const cache = $cacheFactory('hotelfromSessionCache');

        const service: HotelFromSessionService = {
            getHotel,
            getRoomTypes
        };

        const fieldFlags = fieldGroupService.getFieldGroup(
            FieldType.Global,
            FieldType.Titles,
            FieldType.IndicativePrice,
            FieldType.ReviewSnippets,
            FieldType.Description,
            FieldType.Url,
            FieldType.Summary,
            FieldType.Characteristics,
            FieldType.Distances,
            FieldType.Facilities,
            FieldType.LegacyFacilities,
            FieldType.RoomDetails,
            FieldType.Pricing,
            FieldType.RoomAvailability,
            FieldType.Recommendations,
            FieldType.EstabGroupIds,
            FieldType.Airports,
            FieldType.HotelAwards
        );

        function getSkywardsTier() {
            const SkywardsLoginUser = JSON.parse(sessionStorage.getItem('skywardsLoginUser') || '{}');
            const Tier = (SkywardsLoginUser && SkywardsLoginUser.data && SkywardsLoginUser.data.Contact) ?
            SkywardsLoginUser.data.Contact.Tier : null;
            return Tier;
        }

        function getHotel(searchId: string, estabId: number, tripId: string): ng.IPromise<HotelWithRooms> {
            return _getHotelFromSession(searchId, estabId, tripId);
        }

        function getRoomTypes(estabId: number, searchId: string, boardTypeFilter: BoardType[]) {
            const url = '/api/hotels/availability/gethotel';
            const flags = fieldGroupService.getFieldGroup(
                FieldType.RoomAvailability, FieldType.RoomDetails, FieldType.LegacyFacilities
            );
            let request: any = {
                Id: estabId,
                AvailabilitySearchId: searchId,
                CultureCode: TRPCONFIG.Domain.CultureCode,
                CurrencyCode: TRPCONFIG.Domain.CurrencyCode,
                DomainId: TRPCONFIG.Domain.DomainId,
                FieldFlags: flags
            };

            if (TRPCONFIG.Domain2.SkywardsTierPromoEnabled) {
                request.LoyaltyInfo = {
                    SkywardsTier: getSkywardsTier()
                };
            }

            return $http.post<ApiHotelWithRooms>(url, request)
                .then(res => res.data)
                .then(res => {
                    if (!res || !res.RequestedRooms) {
                        return [];
                    }
                    return res.RequestedRooms[0].Availability.map(r => {
                        _.assignIn(r, {
                            RoomDetails: _.find<RoomDetails>(res.RoomDetails, rd => rd.MasterId === r.MasterId)
                        });

                        const uniqBoardTypes = {};
                        r.BoardOptions.forEach(bt => {
                            const current = uniqBoardTypes[bt.BoardType];
                            // is the unique cheapest boardType
                            // is in the filtered boardtypes if defined
                            if ((!current || current.Cost > bt.Cost) &&
                                (!boardTypeFilter.length || boardTypeFilter.indexOf(bt.BoardType) > -1)) {
                                uniqBoardTypes[bt.BoardType] = bt;
                            }
                        });

                        r.BoardOptions = _.values(uniqBoardTypes);

                        return r;
                    }).filter(r => r.BoardOptions.length > 0);
                });
        }

        function _createCacheKey({DiscountCode, Id, AvailabilitySearchId, BoardTypesFilter}) {
            return `${DiscountCode},${Id}, ${AvailabilitySearchId}, ${BoardTypesFilter.sort().join('')}`;
        }

        function _getHotelFromSession(searchId: string,
                                      estabId: number,
                                      tripId: string): ng.IPromise<HotelWithRooms> {
            const hs = $injector.get('hotelSearch'); //this is absolutely fine. Not dodgy at all.
            const url = '/api/hotels/availability/gethotel';
            let request: any = {
                Id: estabId,
                AvailabilitySearchId: searchId,
                CultureCode: TRPCONFIG.Domain.OverrideAPIHotelsCulture ?
                            TRPCONFIG.Domain.DefaultPosCultureCode : TRPCONFIG.Domain.CultureCode,
                CurrencyCode: TRPCONFIG.Domain.CurrencyCode,
                DomainId: TRPCONFIG.Domain.DomainId,
                BoardTypesFilter: hs.getFilter().BoardTypes,
                FieldFlags: fieldFlags,
                DiscountCode: TRPCONFIG.DiscountCode,
                TripId: tripId
            };

            if (TRPCONFIG.Domain2.SkywardsTierPromoEnabled) {
                request.LoyaltyInfo = {
                    SkywardsTier: getSkywardsTier()
                };
            }

            const cacheKey = _createCacheKey(request);
            const cachedHotel = cache.get<HotelWithRooms>(cacheKey);
            if (cachedHotel && TRPCONFIG.Domain.DefaultHotelDates) {
                return $q.when<HotelWithRooms>(cachedHotel);
            } else {
                // Updated AvailabilitySearchID after adjust Dates (Nights Allocation)
                tripService.getTrip(tripId).then(function(trip) {
                    trip && trip.HotelsRequest ? request.AvailabilitySearchId =
                    trip.HotelsRequest.AvailabilitySearchId : '';
                 });
            }

            const d = $q.defer<HotelWithRooms>();
            const start = new Date().getTime();
            const interval = 1000;
            const timeout = 60000;
            let goneResponses = 0;
            return $q.all([
                tripService.getCurrency(tripId),
                tripService.getHotelsRequestNumberOfNights(tripId),
                tripService.getTripOccupancy(tripId),
                tripService.isKidsFree(tripId)
            ]).then(([c, n, o, k]) => {
                request.CurrencyCode = c || TRPCONFIG.Domain.CurrencyCode;
                $http.get('/api/hotels/boardtypes/inclusions').then(
                    res => {
                        sessionStorage.setItem('incExcList', JSON.stringify(res.data));
                        getResult(c, n, o, k);
                    }, err => {
                        sessionStorage.setItem('incExcList', JSON.stringify([]));
                        getResult(c, n, o, k);
                    }
                );

                $http.get('/api/hotels/boardtypes/get').then(
                    res => {
                        let formatedData:any = [];
                        for (let i = 0; i < res.data.length; i ++) {
                            let data = res.data[i];
                            let title = res.data[i].Title;

                            if (title) {
                                data.Title = title.toLowerCase();
                                formatedData.push(data);
                            }
                        }

                        sessionStorage.setItem('boardList', JSON.stringify(formatedData));
                        TRPCONFIG.Domain.BoardTypes = JSON.stringify(formatedData);
                    }, err => {
                        sessionStorage.setItem('boardList', JSON.stringify([]));
                    }
                );
                return d.promise;
            });

            function getResult(currency: string, nights: number, occupancy: any, isKidsFree: boolean = false) {
                $http.post<ApiHotelWithRooms>(url, request, {ignoreErrors: true})
                    .then(_processHotelFactory(d, estabId, cacheKey, currency, nights, occupancy, isKidsFree),
                        _handleErrors);
            }

            function _handleErrors(res): void | ng.IPromise<any> {
                switch (res.status) {
                    case 404:
                        return _notFound();
                    case 410:
                        return _gone();
                }
                return $q.when();
            }

            function logMissing(code) {
                tripService.getHotelsRequest(tripId).then(req => {
                    loggingService.logWarn(
                        `${code} received getting hotel from session for request:
                        ${req ? JSON.stringify(req) : null}`);
                });
            }

            function _notFound(): void | ng.IPromise<any> {
                const now = new Date().getTime();
                if (now - start < timeout) {
                    $timeout(getResult, interval);   //recurse
                    return $q.reject();
                }
                //resolve even in case of no results!
                logMissing('404');
                return d.resolve();
            }

            function _gone() {
                if (goneResponses === 0) {
                    goneResponses += 1;
                    $timeout(getResult, interval);   //recurse
                    return $q.reject();
                }
                logMissing('410');
                d.reject();
                $location.path('hotels/notfound');
            }
        }

        function _processHotelFactory(d: ng.IDeferred<HotelWithRooms>,
                                      estabId: number,
                                      cacheKey: string,
                                      currency: string,
                                      nights: number,
                                      occupancy: any,
                                      isKidsFree: boolean = false) {
            return (result: ng.IHttpPromiseCallbackArg<ApiHotelWithRooms>) => {
                let hotel: HotelWithRooms;

                if (result.data != null) {
                    const dim = TRPCONFIG.DeviceSettings.HotelAvailabilityImageSize;
                    const numPeoplePaying = isKidsFree
                        ? occupancyService.getOnlyPayingIndividuals()
                        : occupancyService.getNumberOfNonInfantPassengers();

                    hotel = _formatHotelData(result.data, occupancy);
                    hotel.ImageUrl = imageService.getMainImageUrl(estabId, dim, dim);
                    hotel.MinCostStr = $filter('roundedCurrency')(hotel.MinCost, currency);
                    hotel.OriginalCostStr = $filter('roundedCurrency')(hotel.OriginalCost, currency);

                    hotel.MinCostPerNight = hotel.MinCost / nights;
                    hotel.MinCostPerPerson = hotel.MinCost / (numPeoplePaying || 1);
                    hotel.MinCostPerPersonPerNight = hotel.MinCostPerPerson / nights;

                    hotel.OriginalCostPerNight = hotel.OriginalCost / nights;
                    hotel.OriginalCostPerPerson = hotel.OriginalCost / (numPeoplePaying || 1);
                    hotel.OriginalCostPerPersonPerNight = hotel.OriginalCostPerPerson / nights;

                    hotel.MinCostStr =
                        $filter('roundedCurrency')(hotel.MinCost, currency);
                    hotel.MinCostPerNightStr =
                        $filter('roundedCurrency')(hotel.MinCostPerNight, currency);
                    hotel.MinCostPerPersonPerNightStr =
                        $filter('roundedCurrency')(hotel.MinCostPerPersonPerNight, currency);

                    hotel.OriginalCostStr =
                        $filter('roundedCurrency')(hotel.OriginalCost, currency);
                    hotel.OriginalCostPerNightStr =
                        $filter('roundedCurrency')(hotel.OriginalCostPerNight, currency);
                    hotel.OriginalCostPerPersonPerNightStr =
                        $filter('roundedCurrency')(hotel.OriginalCostPerPersonPerNight, currency);

                    hotel.LowDepositValue = 0;
                    hotel.IsPayAtHotelOnly = true;

                    hotel.Rooms.filter(rr => !_.isEmpty(rr.Availability))
                        .forEach((rr: HotelRoom, i) => {
                            let minRoomDeposit = 0;
                            let cheapest: RoomAvailability | undefined;
                            _.forEach<RoomAvailability>(rr.Availability, (room: RoomAvailability) => {
                                room.CostPerNight = room.Cost / nights;
                                room.OriginalCostPerNight = room.OriginalCost / nights;
                                if (hotel.IsPayAtHotelOnly && room.IsPayAtHotel === false) {
                                    hotel.IsPayAtHotelOnly = false;
                                }
                                if (room.LowDepositValue
                                    && (!minRoomDeposit || room.LowDepositValue < minRoomDeposit)) {
                                    minRoomDeposit = room.LowDepositValue;
                                }
                                if (!cheapest || cheapest.Cost > room.Cost) {
                                    cheapest = room;
                                }
                            });

                            if (cheapest !== undefined) {
                                cheapest.IsCheapest = true;
                            }

                            if (minRoomDeposit) {
                                hotel.LowDepositValue = hotel.LowDepositValue + minRoomDeposit;
                            }
                        });

                    cache.put<HotelWithRooms>(cacheKey, hotel);
                    d.resolve(hotel);
                    return hotel;
                }
                d.resolve();
                return null;
            };
        }

        function _formatHotelData(data: ApiHotelWithRooms, occupancy: any): HotelWithRooms {
            // Convert room data in response to flat list
            let hotel = _.assign<any, ApiHotelWithRooms, HotelWithRooms>({}, data);
            const incExcList = JSON.parse(sessionStorage.getItem('incExcList') || '[]');
            hotel.Rooms = _.map(data.RequestedRooms, (rr, i) => {
                const availableRoomsList: RoomAvailability[] = [];

                function boardOptionMapping(option, incExcList) {
                    option.map(boardOption => {
                        const item = incExcList
                            .find(item => {
                                if (item.Id === boardOption.InclusionId) {
                                    boardOption.Title =
                                        item.Title.replace('{0}', boardOption.ReplacementText || '');
                                }
                            });
                        return boardOption;
                    });
                }

                rr.Availability.forEach(room => {
                    room.BoardOptions.forEach(option => {
                        if (incExcList && incExcList.length > 0) {
                            if (option.BTInclusions && option.BTInclusions.length > 0) {
                                boardOptionMapping(option.BTInclusions, incExcList);
                            }
                            if (option.BTExclusions && option.BTExclusions.length > 0) {
                                boardOptionMapping(option.BTExclusions, incExcList);
                            }
                        }

                        const ra: RoomAvailability =
                            _.omit<RoomAvailability, ApiRoomAvailability>(room, 'BoardOption');
                        availableRoomsList.push(
                            _.assign<any, BoardOption, RoomAvailability, RoomAvailability>(
                                {},
                                option,
                                ra)
                        );
                    });
                });

                return {
                    Title: occupancyService.getOccupancyStringForRoom(i, occupancy),
                    Availability: availableRoomsList
                };
            });

            return hotel;
        }

        return service;
    }
}
